import Backbone from 'backbone';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FileOpenParams} from 'file-browser/FileBrowserActions';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {getApolloClient} from 'graphql-client/ApolloClient';
import * as navigation from 'Navigation';
import {asJQueryPromise} from 'util/Promises';

import {canvasDefinitionURL, canvasVersionURL} from '../CanvasURL';
import {
  CanvasDefinitionFileStateQuery,
  CanvasDefinitionFileStateQueryVariables,
} from './__generated/CanvasDefinitionFileStateQuery';
import {
  canvasDefinitionFileStateDataToAttributes,
  getCanvasDefinitionFileStateQueryOptions,
} from './CanvasDefinitionGraphQL';
import CanvasDefinitionTabState from './CanvasDefinitionTabState';

interface CanvasDefinitionFileStateAttributes {
  name?: string;
  versionApiId?: string;
}

class CanvasDefinitionFileState extends ApolloFileState<
  CanvasDefinitionFileStateQuery,
  CanvasDefinitionFileStateQueryVariables,
  CanvasDefinitionFileStateAttributes
> {
  static fileType = ModelTypeConstants.CANVAS_DEFINITION;

  static override getQueryOptions(
    fileId: string
  ): ReturnType<typeof getCanvasDefinitionFileStateQueryOptions> {
    return getCanvasDefinitionFileStateQueryOptions(fileId);
  }

  static override dataToAttributes(
    data: CanvasDefinitionFileStateQuery
  ): CanvasDefinitionFileStateAttributes {
    return canvasDefinitionFileStateDataToAttributes(data);
  }

  static override openFile(
    fileId: string,
    queryKey?: FileOpenParams
  ): JQueryPromise<CanvasDefinitionFileState> {
    return asJQueryPromise(getApolloClient().query(this.getQueryOptions(fileId))).then(
      (response) =>
        new this({
          id: fileId,
          versionApiId: queryKey?.versionApiId,
          ...(response != null ? this.dataToAttributes(response.data) : {}),
        })
    );
  }

  canvasTab!: CanvasDefinitionTabState;

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
    };
  }

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  override initialize(...args: any): void {
    super.initialize(...args);

    this.canvasTab = new CanvasDefinitionTabState({
      canvasDefinitionId: this.id,
      versionApiId: this.get('versionApiId'),
    });

    this.on('change:versionApiId', () => {
      this.canvasTab.set('versionApiId', this.get('versionApiId'));
    });

    const buffers = new navigation.BufferStates([
      new navigation.BufferState(),
      new navigation.BufferState({active: false}),
    ]);
    this.set({buffers});
    const buffer = buffers.at(0);
    buffer.tabs().add(this.canvasTab);
    buffer.tabs().at(0).set({active: true});
  }

  override editURL(): string {
    const versionApiId = this.get('versionApiId');
    if (versionApiId != null) {
      return canvasVersionURL(this.id, versionApiId);
    }
    return canvasDefinitionURL(this.id);
  }

  override name(): string {
    return this.get('name') ?? 'Loading...';
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name change:versionApiId', () => {
      this.syncTitleAndURL();
    });
  }
}

export default CanvasDefinitionFileState;
